import { PinIcon } from "@xola/icons";
import { ButtonGroup, ComboBox, Label } from "@xola/ui-kit";
import { getTimezoneAbbreviation, getTimezoneName, isValidTimeZoneId, now } from "common";
import { getCountryForTimezone } from "countries-and-timezones";
import dayjs from "dayjs";
import React, { useState } from "react";
import { components, ValueContainerProps } from "react-select";
import { Geo } from "types";
import { useSeller } from "../../hooks/useAccount";
import { NoTranslate } from "../Common/NoTranslate";

export enum TimezoneType {
    GUEST_TIMEZONE = 0,
    SELLER_TIMEZONE = 1,
}

interface TimezonePickerProps {
    [key: string]: any;
    placeholder?: string;
    className?: string;
    isLoading?: boolean;
    activeTimezone?: number;
    value?: string | { value: string; label: string; geo: Geo };
    onTimezoneChange: (selectedTimezone?: string) => void;
    onInputChange?: (value: string) => void;
    onChange?: (option: { value: string; label: string; geo: Geo } | null) => void;
}

export const TimezonePicker = ({
    placeholder = "Guest's City",
    activeTimezone = TimezoneType.GUEST_TIMEZONE,
    isLoading = false,
    className,
    value,
    onTimezoneChange,
    onChange,
    onInputChange,
    ...rest
}: TimezonePickerProps) => {
    const seller = useSeller();
    const [guestTimezone, setGuestTimezone] = useState(value);

    const sellerTimezoneId = seller.meta.geo.timezone?.timeZoneId;
    const guestTimezoneId = typeof guestTimezone === "string" ? guestTimezone : guestTimezone?.geo.timezone?.timeZoneId;

    const initialTimezones = {
        guest: {
            index: 0,
            abbr: getTimezoneAbbreviation(getTimezoneName(dayjs().tz(guestTimezoneId))),
            timezoneId: guestTimezoneId,
        },
        seller: { index: 1, abbr: getTimezoneAbbreviation(getTimezoneName(now())), timezoneId: sellerTimezoneId },
    };

    const [activeIndex, setActiveIndex] = useState(activeTimezone);
    const [timezones, setTimezones] = useState(initialTimezones);

    const isSameTimezone = timezones.guest.timezoneId === timezones.seller.timezoneId;
    const isVisible = isValidTimeZoneId(timezones.guest.timezoneId) && !isSameTimezone && !isLoading;

    const getTimezone = (timezoneType: TimezoneType) => {
        switch (timezoneType) {
            case TimezoneType.GUEST_TIMEZONE:
                return timezones.guest;

            case TimezoneType.SELLER_TIMEZONE:
                return timezones.seller;

            default:
                return timezones.seller;
        }
    };

    const handleTimezoneChange = (index: number) => {
        setActiveIndex(index);
        onTimezoneChange(getTimezone(index).timezoneId);
    };

    const handleChange = (option: { value: string; label: string; geo: Geo }) => {
        const timezone = option.geo.timezone?.timeZoneId;
        const date = dayjs().tz(timezone);

        if (timezone) {
            setTimezones({
                ...timezones,
                guest: {
                    ...timezones.guest,
                    abbr: getTimezoneAbbreviation(getTimezoneName(date)),
                    timezoneId: timezone,
                },
            });

            if (activeIndex === TimezoneType.GUEST_TIMEZONE) {
                onTimezoneChange(timezone);
            }

            setGuestTimezone(option);
            onChange?.(option);
        }
    };

    return (
        <div className="flex flex-row items-center gap-3">
            <div>
                <Label>Customer Location (for timezone)</Label>
                <ComboBox
                    components={{
                        IndicatorSeparator: () => null,
                        DropdownIndicator: () => null,
                        ValueContainer,
                    }}
                    styles={{
                        control: (base: Record<string, unknown>) => ({
                            ...base,
                            minHeight: 40,
                        }),
                        menu: (base: any) => ({ ...base, zIndex: 30 }),
                        singleValue: (base: any) => ({ ...base, padding: "5px 0" }),
                    }}
                    placeholder={placeholder}
                    className={className}
                    isLoading={isLoading}
                    value={getValue(guestTimezone)}
                    onChange={handleChange}
                    onInputChange={onInputChange}
                    {...rest}
                />
            </div>
            {isVisible && (
                <div>
                    <Label>Show Times In</Label>
                    <ButtonGroup className="h-10" size="medium" value={activeIndex} onChange={handleTimezoneChange}>
                        <ButtonGroup.Button>{timezones.guest.abbr}</ButtonGroup.Button>
                        <ButtonGroup.Button>{timezones.seller.abbr}</ButtonGroup.Button>
                    </ButtonGroup>
                </div>
            )}
        </div>
    );
};

const getValue = (guestTimezone?: string | { value: string; label: string; geo: Geo }) =>
    typeof guestTimezone === "string"
        ? { value: guestTimezone, label: getCountryTimezoneString(guestTimezone) }
        : guestTimezone;

const ValueContainer = ({ children, ...props }: ValueContainerProps) => {
    return (
        components.ValueContainer && (
            <components.ValueContainer {...props}>
                <div className="flex items-center">
                    <div>
                        <PinIcon size="medium" />
                    </div>
                    <NoTranslate as="div" className="grid items-center">
                        {children}
                    </NoTranslate>
                </div>
            </components.ValueContainer>
        )
    );
};

/**
 * Return country + timezone label using just timezoneId
 *
 * @param timezoneId Format "America/Los_Angeles"
 * @returns {string} Format "USA (PDT)"
 */
const getCountryTimezoneString = (timezoneId: string): string => {
    const country = getCountryForTimezone(timezoneId);
    const countryName = country?.name.replace("United States of America", "USA");
    const timezoneAbbreviation = getTimezoneAbbreviation(getTimezoneName(dayjs().tz(timezoneId)));

    return `${countryName} (${timezoneAbbreviation})`;
};
